import { icon } from '../../icons';
import { t } from '../../services/i18n';
import { esc } from './onboarding-shared';

export class RcOnboardingStepDocument extends HTMLElement {
  createdDocumentId: string | null = null;
  createdDocumentName: string | null = null;

  render(): void {
    const hasExisting = !!this.createdDocumentId;
    this.innerHTML = `
      <div class="wizard-step-content">
        <h1 class="wizard-step-title">${t('onboarding.document.title')}</h1>
        <p class="wizard-step-desc">${t('onboarding.document.description')}</p>
        <form id="wizard-form" class="wizard-form" novalidate>
          <div class="form-group">
            <label for="w-doctype">${t('onboarding.document.typeLabel')} <span class="required">*</span></label>
            <select id="w-doctype" name="documentType">
              <option value="Epc">${t('document.type.epc')}</option>
              <option value="Inspection">${t('document.type.inspection')}</option>
              <option value="Insurance">${t('document.type.insurance')}</option>
              <option value="Invoice">${t('document.type.invoice')}</option>
              <option value="Other">${t('document.type.other')}</option>
            </select>
          </div>
          ${hasExisting ? `
            <div class="wizard-uploaded-notice">
              ${icon('check', { size: 16 })}
              ${t('onboarding.document.uploadedNotice')}: <strong>${esc(this.createdDocumentName || t('common.document').toLowerCase())}</strong>
              <span class="wizard-uploaded-hint">${t('onboarding.document.uploadedHint')}</span>
            </div>
          ` : ''}
          <div class="form-group">
            <label for="w-file">${t('common.file')} ${hasExisting ? `<span class="optional">${t('onboarding.document.optionalReplace')}</span>` : '<span class="required">*</span>'}</label>
            <div class="file-drop-zone" id="file-drop-zone">
              <div class="file-drop-icon">${t('onboarding.document.fileDropIcon')}</div>
              <div class="file-drop-text">${t('onboarding.document.fileDropText')}</div>
              <div class="file-drop-hint">${t('onboarding.document.fileDropHint')}</div>
              <input id="w-file" name="file" type="file" accept=".pdf,.jpg,.jpeg,.png" class="file-input-hidden">
            </div>
            <div id="file-selected" class="file-selected" style="display:none"></div>
          </div>
          <div class="form-group">
            <label for="w-expiry">${t('common.expiryDate')} <span class="optional">${t('common.optional')}</span></label>
            <input id="w-expiry" name="expiryDate" type="date">
          </div>
          <div id="wizard-error" class="wizard-error" style="display:none"></div>
          <div class="wizard-actions">
            <button type="button" class="btn btn-outline wizard-prev-btn">
              ${icon('arrow-left', { size: 16, strokeWidth: 2.5 })}
              ${t('common.previous')}
            </button>
            <div class="wizard-actions-right">
              <button type="button" class="btn btn-outline wizard-skip-btn">${t('onboarding.skipStep')}</button>
              <button type="submit" class="btn btn-primary btn-lg wizard-next-btn">
                ${t('onboarding.document.next')}
                ${icon('arrow-right', { size: 16, strokeWidth: 2.5 })}
              </button>
            </div>
          </div>
        </form>
      </div>
    `;

    this.querySelector('.wizard-prev-btn')?.addEventListener('click', () => this.dispatchEvent(new CustomEvent('onboarding-prev', { bubbles: true })));
    this.querySelector('.wizard-skip-btn')?.addEventListener('click', () => this.dispatchEvent(new CustomEvent('onboarding-skip', { bubbles: true })));
    this.querySelector<HTMLFormElement>('#wizard-form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.dispatchEvent(new CustomEvent('onboarding-submit', {
        bubbles: true,
        detail: { form: event.currentTarget as HTMLFormElement },
      }));
    });

    const dropZone = this.querySelector('#file-drop-zone');
    const fileInput = this.querySelector<HTMLInputElement>('#w-file');
    if (dropZone && fileInput) {
      dropZone.addEventListener('click', () => fileInput.click());
      dropZone.addEventListener('dragover', (event) => {
        event.preventDefault();
        dropZone.classList.add('drag-over');
      });
      dropZone.addEventListener('dragleave', () => dropZone.classList.remove('drag-over'));
      dropZone.addEventListener('drop', (event: Event) => {
        event.preventDefault();
        dropZone.classList.remove('drag-over');
        const dt = (event as DragEvent).dataTransfer;
        if (dt?.files.length) {
          fileInput.files = dt.files;
          this.showSelectedFile(dt.files[0]);
        }
      });
      fileInput.addEventListener('change', () => {
        if (fileInput.files?.length) this.showSelectedFile(fileInput.files[0]);
      });
    }
  }

  private showSelectedFile(file: File): void {
    const dropZone = this.querySelector('#file-drop-zone') as HTMLElement | null;
    const selected = this.querySelector('#file-selected') as HTMLElement | null;
    if (dropZone) dropZone.style.display = 'none';
    if (selected) {
      selected.style.display = 'flex';
      selected.innerHTML = `
        <span class="file-selected-icon">${t('common.file')}</span>
        <span class="file-selected-name">${esc(file.name)}</span>
        <span class="file-selected-size">${(file.size / 1024).toFixed(0)} KB</span>
        <button type="button" class="file-remove-btn" title="${t('common.delete')}">x</button>
      `;
      selected.querySelector('.file-remove-btn')?.addEventListener('click', () => {
        const input = this.querySelector<HTMLInputElement>('#w-file');
        if (input) {
          input.value = '';
          (input as { files: FileList | null }).files = null;
        }
        if (dropZone) dropZone.style.display = '';
        selected.style.display = 'none';
      });
    }
  }
}

if (!customElements.get('rc-onboarding-step-document')) {
  customElements.define('rc-onboarding-step-document', RcOnboardingStepDocument);
}
